/**
 * Validate a backend-supplied SSO login URL before navigating the browser to it.
 *
 * The SSO login URL is returned by the backend (either a relative app path such
 * as `/api/v1/sso/login/...` or, in principle, an absolute same-origin URL).
 * Because a malicious or misconfigured backend value could redirect the user to
 * an attacker-controlled site (open redirect), we only allow:
 *
 *   1. A relative path that starts with a single `/` (but NOT `//`, which is a
 *      protocol-relative URL pointing at another host).
 *   2. An absolute URL whose origin exactly matches `window.location.origin`.
 *
 * Everything else — protocol-relative `//evil.com`, the backslash variant
 * `/\evil.com` (browsers normalize `\`→`/`, so this resolves to a foreign host),
 * cross-origin absolute URLs, and dangerous schemes like `javascript:` / `data:`
 * — is rejected.
 *
 * The final decision for EVERY accepted URL is an origin comparison against
 * `window.location.origin` (relative inputs are resolved against it first), so
 * any browser URL-normalization quirk that would escape to another host is
 * caught rather than trusted on the string prefix alone.
 */
export function isSafeSsoUrl(url: string): boolean {
  if (typeof url !== "string" || url.length === 0) {
    return false;
  }

  // Reject anything that opens with two "slashes" (protocol-relative `//host`,
  // or `/\host` / `\\host` — browsers normalize backslashes to forward slashes,
  // so all three resolve to a foreign origin). This must come before the
  // single-`/` relative check below.
  if (/^[/\\]{2}/.test(url) || /^\/[\\]/.test(url)) {
    return false;
  }

  // Resolve relative paths and absolute URLs alike against the current origin and
  // require the result to stay on it. `new URL` collapses dangerous/malformed
  // schemes (`javascript:`, `data:`) to a `"null"` origin that will not match.
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}
